import { useState, useCallback, useMemo, useRef } from "react";
import { publishSchedulerEvents } from "repositories/scheduler-repository";
import { handleError, handleSuccess } from "utils";
import {
  dedupeDraftPublishItems,
  getPublishDateRangeLabel,
  getPublishItemCounts,
  getPublishItemTypeLabel,
  getSelectedPublishableDraftItems,
} from "./eventsWorkspacePublish.utils";
import { getClearableItemDedupeKey } from "./eventsWorkspaceClear.utils";

const areDraftListsEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  const leftKeys = left
    .map((item) => getClearableItemDedupeKey(item))
    .filter(Boolean)
    .sort()
    .join(",");
  const rightKeys = right
    .map((item) => getClearableItemDedupeKey(item))
    .filter(Boolean)
    .sort()
    .join(",");
  return leftKeys === rightKeys;
};

/**
 * Global / selected publish flow for Event Workspace (all calendar views).
 * @param {{
 *   t: Function,
 *   calendarViewId: string,
 *   currentDate: Date,
 *   selectedEvents: Record<string, { id: number, isDraft?: boolean }>,
 *   onRefresh?: () => void,
 *   onClearSelection?: () => void,
 * }} options
 */
export function useEventsWorkspacePublish({
  t,
  calendarViewId,
  currentDate,
  selectedEvents,
  onRefresh,
  onClearSelection,
}) {
  const [visibleDraftItems, setVisibleDraftItems] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const isPublishingRef = useRef(false);

  const handleVisibleDraftEventsUpdate = useCallback((draftItems = []) => {
    const normalized = dedupeDraftPublishItems(draftItems);
    setVisibleDraftItems((prev) =>
      areDraftListsEqual(prev, normalized) ? prev : normalized,
    );
  }, []);

  const canPublishGlobal = visibleDraftItems.length > 0 && !isPublishing;

  /** Same eligibility as Global: drafts that appear in the visible publishable set. */
  const selectedPublishableDraftItems = useMemo(
    () => getSelectedPublishableDraftItems(selectedEvents, visibleDraftItems),
    [selectedEvents, visibleDraftItems],
  );

  const canPublishSelected =
    selectedPublishableDraftItems.length > 0 && !isPublishing;

  const dateRangeLabel = useMemo(
    () => getPublishDateRangeLabel(calendarViewId, currentDate),
    [calendarViewId, currentDate],
  );

  const closeConfirmModal = useCallback(() => {
    if (isPublishingRef.current) return;
    setConfirmModal(null);
  }, []);

  const executePublish = useCallback(
    async (items) => {
      // Occurrence lists may repeat the same event id across staff cards;
      // the publish API expects unique scheduler event ids.
      const ids = [...new Set(items.map((item) => item.id).filter(Boolean))];
      if (!ids.length || isPublishingRef.current) return;

      isPublishingRef.current = true;
      setIsPublishing(true);
      try {
        await publishSchedulerEvents(ids, true);
        handleSuccess(t("scheduler.publishBulkSuccess"));
        setConfirmModal(null);
        onClearSelection?.();
        onRefresh?.();
      } catch (error) {
        handleError(error?.message || error);
      } finally {
        isPublishingRef.current = false;
        setIsPublishing(false);
      }
    },
    [t, onClearSelection, onRefresh],
  );

  const openGlobalPublishConfirm = useCallback(() => {
    if (!visibleDraftItems.length || isPublishingRef.current) return;
    const counts = getPublishItemCounts(visibleDraftItems);
    setConfirmModal({
      mode: "global",
      items: visibleDraftItems,
      ...counts,
      itemTypeLabel: getPublishItemTypeLabel(counts, t),
    });
  }, [visibleDraftItems, t]);

  const openPublishSelectedConfirm = useCallback(() => {
    if (isPublishingRef.current) return;
    const selectedDraftItems = selectedPublishableDraftItems;
    if (!selectedDraftItems.length) return;

    const counts = getPublishItemCounts(selectedDraftItems);
    setConfirmModal({
      mode: "selected",
      items: selectedDraftItems,
      ...counts,
      itemTypeLabel: getPublishItemTypeLabel(counts, t),
    });
  }, [selectedPublishableDraftItems, t]);

  const confirmPublish = useCallback(() => {
    if (!confirmModal?.items?.length) return;
    executePublish(confirmModal.items);
  }, [confirmModal, executePublish]);

  return {
    visibleDraftItems,
    canPublishGlobal,
    canPublishSelected,
    isPublishing,
    confirmModal,
    dateRangeLabel,
    handleVisibleDraftEventsUpdate,
    openGlobalPublishConfirm,
    openPublishSelectedConfirm,
    closeConfirmModal,
    confirmPublish,
  };
}
